import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Preferences } from '@capacitor/preferences'

import { fetchSupportCode, type SupportCode } from '@/lib/api'

/**
 * Weekly-rotating support code for an anonymous patient. Server is
 * canonical: `GET /api/patient/support-code` lazily creates a row for
 * the current ISO week on first read and returns the same code on
 * subsequent reads inside that week.
 *
 * The on-device cache exists so the settings screen can re-reveal a
 * previously-fetched code offline. The server still rotates the code
 * weekly — once the client comes back online, the next `fetch()` brings
 * the new week's code in and overwrites the cache.
 *
 * Hydration is intentionally NOT wired into app mount; the code is only
 * surfaced behind a deliberate "show support code" tap in
 * PatientSettings, so we fetch on first reveal.
 */
const CACHE_KEY = 'support_code'

export const useSupportCodeStore = defineStore('support-code', () => {
  const code = ref<string | null>(null)
  const weekStartsAt = ref<string | null>(null)
  const status = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const lastError = ref<string | null>(null)

  let inflight: Promise<void> | null = null

  async function readCache(): Promise<SupportCode | null> {
    try {
      const { value } = await Preferences.get({ key: CACHE_KEY })
      if (!value) return null
      return JSON.parse(value) as SupportCode
    } catch {
      return null
    }
  }

  async function writeCache(next: SupportCode): Promise<void> {
    try {
      await Preferences.set({ key: CACHE_KEY, value: JSON.stringify(next) })
    } catch {
      // Cache writes are best-effort.
    }
  }

  /**
   * Fetch the current week's support code, hydrating from cache first
   * for an instant first paint. Concurrent calls share the inflight
   * promise. Errors surface via `lastError`; cached values remain on
   * screen so an offline reveal still works.
   */
  async function fetch(): Promise<void> {
    if (inflight) return inflight
    inflight = (async () => {
      status.value = 'loading'
      const cached = await readCache()
      if (cached) {
        code.value = cached.code
        weekStartsAt.value = cached.week_starts_at
      }

      try {
        const server = await fetchSupportCode()
        code.value = server.code
        weekStartsAt.value = server.week_starts_at
        await writeCache(server)
        status.value = 'ready'
        lastError.value = null
      } catch (e) {
        lastError.value = e instanceof Error ? e.message : String(e)
        status.value = cached ? 'ready' : 'error'
      } finally {
        inflight = null
      }
    })()
    return inflight
  }

  /**
   * Drop the cached code. Called after a device transfer: the code identifies a
   * patient to support staff, and this device now belongs to a different one.
   * The next reveal lazily fetches the surviving account's code.
   */
  async function reset(): Promise<void> {
    code.value = null
    weekStartsAt.value = null
    status.value = 'idle'
    lastError.value = null
    inflight = null
    try {
      await Preferences.remove({ key: CACHE_KEY })
    } catch {
      // Best-effort.
    }
  }

  return {
    code,
    weekStartsAt,
    status,
    lastError,
    fetch,
    reset,
  }
})
